import { type FormEvent, useState } from 'react';
import { ErrorApi, useApi } from '@plataforma/web/api';
import { ETAPAS_COSTO, fechaCorta, mxn } from '../formato';
import type { Costo } from '../paginas/tipos';

/** Bitácora de costos del vehículo por etapa, en su moneda original y en MXN (arquitectura, sección 6). */
export function BitacoraCostos({
  costos,
  vehiculoId,
  alGuardar,
}: {
  costos: { datos: Costo[] | null; error: string | null };
  vehiculoId: string;
  alGuardar: () => void;
}) {
  return (
    <section className="seccion">
      <header>
        <h2>Bitácora de costos</h2>
      </header>
      {costos.error && (
        <p className="error" style={{ margin: 16 }}>
          {costos.error}
        </p>
      )}
      {costos.datos && !costos.datos.length ? (
        <p className="vacio">
          Sin costos todavía. Registra la compra, el traslado o la importación.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Etapa</th>
              <th>Concepto</th>
              <th className="num">Monto original</th>
              <th className="num">MXN</th>
            </tr>
          </thead>
          <tbody>
            {(costos.datos ?? [])
              .filter((c) => !c.anulado)
              .map((c) => (
                <tr key={c.id} style={{ opacity: c.reemplazadoPor ? 0.5 : 1 }}>
                  <td>{fechaCorta(c.fecha)}</td>
                  <td>{ETAPAS_COSTO[c.etapa] ?? c.etapa}</td>
                  <td>
                    {c.concepto}{' '}
                    {c.estimado && (
                      <span className="etiqueta aviso">
                        {c.reemplazadoPor ? 'estimado reemplazado' : 'estimado'}
                      </span>
                    )}
                  </td>
                  <td className="num">
                    {c.monto.moneda === 'USD'
                      ? `USD ${Number(c.monto.cantidad).toLocaleString('es-MX', { minimumFractionDigits: 2 })} · TC ${c.monto.tipoCambio}`
                      : mxn(c.monto.cantidad)}
                  </td>
                  <td className="num">{mxn(c.monto.equivalenteMxn)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      <NuevoCosto vehiculoId={vehiculoId} alGuardar={alGuardar} />
    </section>
  );
}

function NuevoCosto({ vehiculoId, alGuardar }: { vehiculoId: string; alGuardar: () => void }) {
  const api = useApi();
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format();
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const f = new FormData(forma);
    setEnviando(true);
    setError(null);
    try {
      await api('POST', `/finance/vehiculos/${vehiculoId}/costos`, {
        etapa: f.get('etapa'),
        concepto: f.get('concepto'),
        cantidad: String(f.get('cantidad')),
        moneda: f.get('moneda'),
        fecha: f.get('fecha'),
        estimado: f.get('estimado') === 'on',
      });
      forma.reset();
      alGuardar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo registrar');
    } finally {
      setEnviando(false);
    }
  };
  return (
    <form
      className="cuerpo formulario"
      onSubmit={(e) => void enviar(e)}
      style={{ borderTop: '1px solid var(--borde-suave)' }}
    >
      <label className="campo">
        <span>Etapa</span>
        <select name="etapa" defaultValue="traslado">
          {Object.entries(ETAPAS_COSTO).map(([clave, texto]) => (
            <option key={clave} value={clave}>
              {texto}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Concepto</span>
        <input
          name="concepto"
          required
          minLength={2}
          maxLength={120}
          placeholder="Grúa de Houston a Laredo"
        />
      </label>
      <label className="campo">
        <span>Cantidad</span>
        <input
          name="cantidad"
          required
          inputMode="decimal"
          pattern="\d{1,12}(\.\d{1,2})?"
          placeholder="1250.00"
        />
      </label>
      <label className="campo">
        <span>Moneda</span>
        <select name="moneda" defaultValue="USD">
          <option value="USD">USD (FIX de Banxico de la fecha)</option>
          <option value="MXN">MXN</option>
        </select>
      </label>
      <label className="campo">
        <span>Fecha</span>
        <input name="fecha" type="date" required defaultValue={hoy} />
      </label>
      <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
        <input name="estimado" type="checkbox" style={{ minHeight: 0 }} />
        <span>Es estimado (se reemplaza al llegar el real)</span>
      </label>
      {error && (
        <p className="error" style={{ gridColumn: '1 / -1' }}>
          {error}
        </p>
      )}
      <div className="acciones">
        <button className="boton primario" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Registrar costo'}
        </button>
      </div>
    </form>
  );
}
